import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload } from "lucide-react";
import { getConfig } from "@happyspace/sdk/data";

export interface FieldDef {
  key: string;
  label: string;
  type?: "text" | "textarea" | "image" | "select" | "multiselect" | "number" | "url" | "tel";
  placeholder?: string;
  required?: boolean;
  options?: string[]; // for select / multiselect
  help?: string;
}

// Read a chosen image file and downscale/compress it to a compact data URL.
// The data URL is an INTERMEDIATE value only — it must be converted to a
// hosted URL via uploadDataUrlToHost() before it is saved into a card field
// (see below for why).
function fileToDataUrl(file: File, maxDim = 1000, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("Please choose an image file."));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("That file isn't a valid image."));
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(reader.result as string); // fallback: original data URL
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        // PNGs (often logos/transparency) keep PNG when not downscaled; else JPEG.
        const out =
          file.type === "image/png" && scale === 1
            ? canvas.toDataURL("image/png")
            : canvas.toDataURL("image/jpeg", quality);
        resolve(out);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

// Convert a base64 data URL into a HOSTED image URL before it is saved into
// a card field. Storing the raw data URL directly in the field is what made
// image edits fail with "Validation failed": the merged override row sent to
// the data API carried a 150-500KB base64 string, which the API rejected —
// so the UPDATE and the INSERT fallback both failed and the edit rolled back.
//
// The platform's data API itself converts any `data:image/...` value into a
// hosted storage URL when a row is INSERTED (verified against the real API:
// the insert response comes back with the value replaced by a
// storage.googleapis.com URL). So we round-trip the data URL through a
// dedicated scratch table, take the hosted URL out of the response, and save
// THAT into the card — the exact same shape as image URLs that already save
// fine. The scratch row is deleted best-effort afterwards; the hosted file
// itself lives on in storage either way.
async function uploadDataUrlToHost(dataUrl: string): Promise<string> {
  if (typeof window === "undefined") return dataUrl; // SSR/prerender guard
  const config: any = getConfig();
  const sessionId = `shared-${config.businessId}-image_uploads`;
  const rowsUrl = `${config.apiBaseUrl}/public/data/${config.businessId}/image_uploads/rows`;
  // This insert is a WRITE and is rate-limited (429) just like any other — and
  // an admin card edit fires several writes back-to-back, so this upload step
  // is the MOST likely one to hit the cap. Retry transient 429s with backoff
  // (mirrors writeWithRetry in usePreviewSafeData.ts) instead of failing the
  // whole edit. Permanent errors still fail fast.
  const payload = JSON.stringify({ image: dataUrl, uploadedAt: new Date().toISOString() });
  let res: Response | null = null;
  let data: any = {};
  for (let attempt = 0; attempt < 4; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, 1500 * attempt * attempt));
    res = await fetch(rowsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Data-Session": sessionId,
      },
      body: payload,
    });
    data = await res.json().catch(() => ({}));
    if (res.ok || res.status !== 429) break;
  }
  if (!res || !res.ok) {
    throw new Error(data?.error || `Image upload failed (${res ? res.status : "network"})`);
  }
  const hosted: string | undefined = data?.row?.data?.image;
  const rowId: string | undefined = data?.row?._id;
  if (rowId) {
    // Best-effort tidy-up; the hosted file survives the row delete.
    fetch(`${rowsUrl}/${rowId}`, {
      method: "DELETE",
      headers: { "X-Data-Session": sessionId },
    }).catch(() => {});
  }
  if (!hosted || !/^https?:\/\//.test(hosted)) {
    throw new Error("The upload did not return a hosted image URL.");
  }
  return hosted;
}

interface AdminFieldModalProps {
  open: boolean;
  title: string;
  fields: FieldDef[];
  initial?: Record<string, any>;
  onSave: (values: Record<string, any>) => Promise<void> | void;
  onClose: () => void;
}

export default function AdminFieldModal({ open, title, fields: fieldsProp, initial, onSave, onClose }: AdminFieldModalProps) {
  // De-duplicate by key: pages may append a shared field set (e.g. SOCIAL_FIELDS)
  // that overlaps a field they already declared — show each field only once.
  const fields = fieldsProp.filter((f, i, a) => a.findIndex((x) => x.key === f.key) === i);
  const [values, setValues] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [uploadedKeys, setUploadedKeys] = useState<string[]>([]);

  const handleImageFile = async (key: string, file: File | undefined) => {
    if (!file) return;
    setBusyKey(key);
    setError("");
    try {
      const dataUrl = await fileToDataUrl(file);
      // Convert to a hosted URL BEFORE it ever reaches the card payload —
      // raw data URLs are what made image edits fail validation.
      const hosted = await uploadDataUrlToHost(dataUrl);
      setValues((v) => ({ ...v, [key]: hosted }));
      setUploadedKeys((k) => [...new Set([...k, key])]);
    } catch (e: any) {
      setError(e?.message || "Could not process that image.");
    } finally {
      setBusyKey(null);
    }
  };

  useEffect(() => {
    if (open) {
      const seed: Record<string, any> = {};
      for (const f of fields) {
        if (f.type === "multiselect") {
          // The platform datatable declares multiselect fields as `string`
          // columns, so a saved array comes back comma-joined
          // ("Clothing,Accessories"). Wrapping that whole string in an array
          // made every chip render UNSELECTED when reopening a card that
          // already had types — the admin would then re-pick them and
          // toggleMultiValue would APPEND, saving "Clothing,Accessories,
          // Clothing,Accessories". Split it back apart and de-dupe, which also
          // repairs any already-doubled row the next time it is saved.
          // Values not in f.options are deliberately kept so that editing a
          // card never silently drops a tag the options list doesn't cover.
          const raw = initial?.[f.key];
          const parts = Array.isArray(raw)
            ? raw.map((x) => String(x).trim())
            : typeof raw === "string"
            ? raw.split(",").map((x) => x.trim())
            : [];
          seed[f.key] = [...new Set(parts.filter(Boolean))];
        } else {
          seed[f.key] = initial?.[f.key] ?? "";
        }
      }
      setValues(seed);
      setError("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key: string, val: any) => setValues((v) => ({ ...v, [key]: val }));

  const toggleMultiValue = (key: string, option: string) => {
    setValues((v) => {
      const cur: string[] = Array.isArray(v[key]) ? v[key] : [];
      const next = cur.includes(option) ? cur.filter((o) => o !== option) : [...cur, option];
      return { ...v, [key]: next };
    });
  };

  const handleSave = async () => {
    for (const f of fields) {
      if (f.type === "multiselect") {
        if (f.required && !(Array.isArray(values[f.key]) && values[f.key].length > 0)) {
          setError(`"${f.label}" is required.`);
          return;
        }
        continue;
      }
      if (f.required && !String(values[f.key] ?? "").trim()) {
        setError(`"${f.label}" is required.`);
        return;
      }
    }
    setSaving(true);
    setError("");
    // Only persist the schema's fields (avoid leaking stray keys).
    const payload: Record<string, any> = {};
    for (const f of fields) payload[f.key] = values[f.key] ?? "";
    // Close immediately instead of awaiting the underlying persistence.
    // useManagedCollection's saveEdit applies its optimistic UI update
    // SYNCHRONOUSLY (before any network call), so the card already shows
    // the change the instant onSave() is invoked — awaiting the network
    // write here just makes the whole modal hang open if that write is
    // slow or fails (e.g. a real update() call with no preview mock path).
    // That's what made "click Save" appear to do nothing on a default/
    // featured card like Indraprastha. Persist in the background instead
    // and only log a failure — the change is never lost since it's a
    // fresh override row on retry.
    onClose();
    try {
      await onSave(payload);
    } catch (e: any) {
      console.error("[AdminFieldModal] Failed to persist change:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-mc-no-drag
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-xl">
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
              <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
              )}
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    {f.label}
                    {f.required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  {f.type === "textarea" ? (
                    <textarea
                      name={f.key}
                      value={values[f.key] ?? ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082f6d]/20 focus:border-[#082f6d]"
                    />
                  ) : f.type === "select" ? (
                    <select
                      name={f.key}
                      value={values[f.key] ?? ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#082f6d]/20 focus:border-[#082f6d]"
                    >
                      <option value="">Select…</option>
                      {(f.options || []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : f.type === "multiselect" ? (
                    <div className="flex flex-wrap gap-2">
                      {(f.options || []).map((o) => {
                        const selected: string[] = Array.isArray(values[f.key]) ? values[f.key] : [];
                        const isChecked = selected.includes(o);
                        return (
                          <button
                            key={o}
                            type="button"
                            onClick={() => toggleMultiValue(f.key, o)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                              isChecked
                                ? "bg-[#082f6d] border-[#082f6d] text-white"
                                : "bg-white border-gray-200 text-gray-600 hover:border-[#082f6d]/40"
                            }`}
                          >
                            {o}
                          </button>
                        );
                      })}
                    </div>
                  ) : f.type === "image" ? (
                    <div>
                      <input
                        name={f.key}
                        type="text"
                        value={String(values[f.key] ?? "").startsWith("data:") ? "" : (values[f.key] ?? "")}
                        onChange={(e) => set(f.key, e.target.value)}
                        placeholder={f.placeholder || "Paste image URL…"}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082f6d]/20 focus:border-[#082f6d]"
                      />
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-semibold text-[#082f6d] bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors">
                          <Upload className="w-3.5 h-3.5" />
                          Upload photo
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageFile(f.key, e.target.files?.[0])}
                          />
                        </label>
                        {busyKey === f.key && <span className="text-xs text-gray-400">Uploading…</span>}
                        {uploadedKeys.includes(f.key) && values[f.key] && (
                          <span className="text-xs text-green-600 font-medium">📷 Photo uploaded</span>
                        )}
                        {values[f.key] && (
                          <button
                            type="button"
                            onClick={() => set(f.key, "")}
                            className="text-xs text-gray-400 hover:text-red-500"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <input
                      name={f.key}
                      type={f.type === "number" ? "number" : "text"}
                      value={values[f.key] ?? ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082f6d]/20 focus:border-[#082f6d]"
                    />
                  )}
                  {f.type === "image" && values[f.key] ? (
                    <img
                      src={values[f.key]}
                      alt="preview"
                      className="mt-2 h-20 w-32 object-cover rounded-lg border border-gray-100"
                      onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                    />
                  ) : null}
                  {f.help && <p className="text-[11px] text-gray-400 mt-1">{f.help}</p>}
                </div>
              ))}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white rounded-b-xl">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 text-sm font-semibold text-white bg-[#082f6d] hover:bg-[#061f4a] rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
